/**
 * Turbo IQ Admin — "Operations Cockpit" shared primitives.
 * Light theme: carbon rails, cool canvas, white cards w/ hairline borders,
 * Space Mono numerics, Turbo Red as the single action colour.
 */

// ---- Status pill (Space Mono, uppercase, soft fill) ----
const PILL_TONES = {
  success: { bg: '#DCFCE7', fg: '#15803D' },
  info: { bg: '#DBEAFE', fg: '#1D4ED8' },
  viewed: { bg: '#CFFAFE', fg: '#0E7490' },
  warning: { bg: '#FEF3C7', fg: '#92400E' },
  scheduled: { bg: '#EEF2FF', fg: '#4338CA' },
  danger: { bg: '#FEE2E2', fg: '#991B1B' },
  neutral: { bg: '#EDEDF0', fg: '#55555C' },
  cancel: { bg: '#F1F5F9', fg: '#64748B' },
};

export const StatusPill = ({ tone = 'neutral', children, style }) => {
  const t = PILL_TONES[tone] || PILL_TONES.neutral;
  return (
    <span
      className="font-tac-mono inline-flex items-center"
      style={{ background: t.bg, color: t.fg, fontWeight: 700, fontSize: 9.5, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 8px', borderRadius: 999, ...style }}
    >
      {children}
    </span>
  );
};

// ---- AI badge (purple gradient) ----
export const AiBadge = ({ children = 'AI', style }) => (
  <span
    className="font-tac-mono inline-flex items-center"
    style={{ background: 'linear-gradient(135deg,#7C3AED,#A78BFA)', color: '#fff', fontWeight: 700, fontSize: 9, letterSpacing: '0.06em', padding: '3px 7px', borderRadius: 5, ...style }}
  >
    {children}
  </span>
);

// ---- Callout (info / warning / danger / success) ----
const CALLOUT_TONES = {
  info: { bg: '#EFF6FF', border: '#DBEAFE', fg: '#1E40AF' },
  warning: { bg: '#FFF3CD', border: '#FFC107', fg: '#856404' },
  danger: { bg: '#FEF2F2', border: '#FECACA', fg: '#991B1B' },
  success: { bg: '#F0FDF4', border: '#22C55E', fg: '#166534' },
};

export const Callout = ({ tone = 'info', title, children, icon, style }) => {
  const t = CALLOUT_TONES[tone] || CALLOUT_TONES.info;
  return (
    <div className="font-tac-body" style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 12, padding: 14, color: t.fg, ...style }}>
      <div className="flex items-start gap-2.5">
        {icon && <span className="flex-shrink-0 mt-0.5">{icon}</span>}
        <div style={{ fontSize: 13, lineHeight: 1.55 }}>
          {title && <div className="font-tac-heading" style={{ fontWeight: 700, marginBottom: 3 }}>{title}</div>}
          {children}
        </div>
      </div>
    </div>
  );
};

// ---- Card-header chip (gradient tile + title + subtitle + optional right) ----
export const CardHeaderChip = ({ icon, title, subtitle, right, dark = false }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="flex items-center gap-3 min-w-0">
      <div
        className="flex-shrink-0 flex items-center justify-center"
        style={{ width: 38, height: 38, borderRadius: 10, background: dark ? 'linear-gradient(135deg,#2B2B2B,#0A0A0B)' : 'linear-gradient(135deg,#BF212F,#8B1923)' }}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <div className="font-tac-heading" style={{ fontWeight: 700, fontSize: 17, color: '#141414', letterSpacing: '-0.01em' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: '#55555C', marginTop: 1 }}>{subtitle}</div>}
      </div>
    </div>
    {right}
  </div>
);

// ---- Card (white, hairline border, soft shadow) ----
export const Card = ({ children, className = '', style, ...rest }) => (
  <div
    className={`font-tac-body ${className}`}
    style={{ background: '#fff', border: '1px solid #E4E4E9', borderRadius: 14, boxShadow: '0 1px 2px rgba(20,20,20,0.04)', ...style }}
    {...rest}
  >
    {children}
  </div>
);

// ---- Stat tile (bento). hero=true → carbon dark tile. ----
export const StatTile = ({ label, value, detail, icon, hero = false, accent = '#BF212F', onClick }) => (
  <button
    onClick={onClick}
    className="text-left w-full transition-all"
    style={{
      background: hero ? '#0A0A0B' : '#fff',
      border: hero ? '1px solid #0A0A0B' : '1px solid #E4E4E9',
      borderRadius: 14,
      padding: 18,
      cursor: onClick ? 'pointer' : 'default',
    }}
  >
    <div className="flex items-center justify-between">
      <span className="tac-kicker" style={{ fontSize: 10, color: hero ? 'rgba(255,255,255,0.55)' : '#9A9AA0' }}>{label}</span>
      {icon && (
        <span className="flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 8, background: hero ? 'rgba(255,255,255,0.08)' : `${accent}14`, color: accent }}>
          {icon}
        </span>
      )}
    </div>
    <div className="font-tac-mono" style={{ fontSize: 28, fontWeight: 700, color: hero ? '#fff' : '#141414', marginTop: 10, letterSpacing: '-0.01em' }}>
      {value}
    </div>
    {detail && (
      <div style={{ fontSize: 12, color: hero ? 'rgba(255,255,255,0.6)' : '#6B6B72', marginTop: 4, lineHeight: 1.35 }}>
        {detail}
      </div>
    )}
  </button>
);

// ---- Buttons ----
const BTN_SIZES = { sm: '8px 13px', md: '11px 18px', lg: '14px 24px' };
export const Btn = ({ variant = 'primary', size = 'md', full = false, children, style, className = '', ...rest }) => {
  const base = { primary: { background: '#BF212F', color: '#fff', border: '1px solid #BF212F' }, dark: { background: '#0A0A0B', color: '#fff', border: '1px solid #0A0A0B' }, secondary: { background: '#fff', color: '#141414', border: '1px solid #E4E4E9' }, ghost: { background: 'transparent', color: '#BF212F', border: '1px solid transparent' } };
  const v = base[variant] || base.primary;
  return (
    <button
      className={`font-tac-heading inline-flex items-center justify-center gap-2 transition-all ${className}`}
      style={{ ...v, padding: BTN_SIZES[size], borderRadius: 9, fontWeight: 600, fontSize: size === 'sm' ? 12.5 : 13.5, cursor: 'pointer', width: full ? '100%' : undefined, ...style }}
      {...rest}
    >
      {children}
    </button>
  );
};
